import { useState, useEffect, useMemo, useRef } from 'react';
import * as db from './db.js';
import { todayLocal, isoToLocalDate } from './lib/date.js';
import { isAmount, parseAmount, checkWinner } from './lib/money.js';
import { averageOfVisits, toggleWantToTry as toggleWantToTryOf } from './lib/visits.js';
import { pickerPool, weightedPick, spinDelays } from './lib/picker.js';
import { suggestCategories } from './lib/categories.js';
import { AppContext } from './context.js';
import { PASSCODE, themeFor } from './presets.js';
import { computeStats, averageRating as averageRatingOf, ratedCount as ratedCountOf, activeRestaurantCount as activeRestaurantCountOf } from './lib/stats.js';
import { sortRestaurants, deletedRestaurants as deletedRestaurantsOf } from './lib/sort.js';
import { useRestaurants } from './hooks/useRestaurants.js';
import { useSharedState } from './hooks/useSharedState.js';
import PasscodeScreen from './components/PasscodeScreen.jsx';
import ProfilePicker from './components/ProfilePicker.jsx';
import { Toast, TopBanners } from './components/Banners.jsx';
import FloatingBg from './components/FloatingBg.jsx';
import SettingsPanel from './components/SettingsPanel.jsx';
import Header from './components/Header.jsx';
import QuickStats from './components/QuickStats.jsx';
import PlannedDateCard from './components/PlannedDateCard.jsx';
import SearchBar from './components/SearchBar.jsx';
import AddPlaceForm from './components/AddPlaceForm.jsx';
import SortControls from './components/SortControls.jsx';
import TrashList from './components/TrashList.jsx';
import RestaurantList from './components/RestaurantList.jsx';
import StatsTab from './components/StatsTab.jsx';
import VisitModal from './components/VisitModal.jsx';
import EditRestaurantModal from './components/EditRestaurantModal.jsx';
import PlanDateModal from './components/PlanDateModal.jsx';
import RandomPickerModal from './components/RandomPickerModal.jsx';
import AfterDateSheet from './components/AfterDateSheet.jsx';
import ProfilePhotoModal from './components/ProfilePhotoModal.jsx';
import MapView from './components/MapView.jsx';

function RestaurantRater() {
    // 3. Haptic Feedback (Android only - iOS doesn't support web vibration)
    const haptic = (style = 'medium') => {
        if (window.navigator.vibrate) {
            const patterns = {
                light: [10],
                medium: [20],
                heavy: [30],
                success: [10, 50, 10],
                error: [50, 100, 50]
            };
            window.navigator.vibrate(patterns[style] || patterns.medium);
        }
    };

    // Auth & Profile State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentProfile, setCurrentProfile] = useState(null); // 'doug' or 'les'
    const [passcodeInput, setPasscodeInput] = useState('');
    const [showPasscodeError, setShowPasscodeError] = useState(false);
    const [passcodeShake, setPasscodeShake] = useState(false);

    // Connection & Toast State
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }

    // Toast helper (a newer toast replaces the older one and gets its full 3 seconds)
    const toastTimer = useRef(null);
    const showToast = (message, type = 'success') => {
        clearTimeout(toastTimer.current);
        setToast({ message, type });
        toastTimer.current = setTimeout(() => setToast(null), 3000);
    };
    const onWriteError = () => showToast("Couldn't sync that change. It will retry when you're back online.", 'error');


    // Restaurant list: Firebase listener, localStorage cache, per-restaurant writes (hooks/useRestaurants.js)
    const syncEnabled = isAuthenticated && !!currentProfile;
    const { restaurants, setRestaurants, mutate, create, createMany, destroy, sync, isSyncing } = useRestaurants({ enabled: syncEnabled, onWriteError });
    const [newRestaurant, setNewRestaurant] = useState({ name: '', address: '', coordinates: null, placeId: null });
    const [expandedId, setExpandedId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem('sortPreference') || 'name';
    });
    const [activeTab, setActiveTab] = useState('restaurants');
    const [showSettings, setShowSettings] = useState(false);
    const [showFloatingBg, setShowFloatingBg] = useState(() => {
        const saved = localStorage.getItem('showFloatingBg');
        return saved !== null ? saved === 'true' : true;
    });
    const [floatingEmoji, setFloatingEmoji] = useState(() => {
        return localStorage.getItem('floatingEmoji') || '';
    });
    const [colorTheme, setColorTheme] = useState(() => {
        return localStorage.getItem('colorTheme') || 'classic';
    });

    // Plan a Date state
    const [showPlanDateModal, setShowPlanDateModal] = useState(false);
    const [planDateForm, setPlanDateForm] = useState({
        restaurantId: null, restaurantName: '', date: '', time: '', notes: ''
    });
    const [planDateSearch, setPlanDateSearch] = useState('');

    // 1. Search/Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [showUnratedOnly, setShowUnratedOnly] = useState(false);
    const [wantFilter, setWantFilter] = useState('none'); // 'none' | 'both'
    const [cuisineFilter, setCuisineFilter] = useState([]);
    const [vibeFilter, setVibeFilter] = useState([]);
    const [showTrash, setShowTrash] = useState(false);

    // Firebase-synced shared state (profile photos, active game + my inputs, planned date) and the listeners (hooks/useSharedState.js)
    const {
        profilePhotos, setProfilePhotos,
        activeGame, setActiveGame,
        myGuess, setMyGuess,
        actualAmount, setActualAmount,
        resultCountdown, setResultCountdown,
        plannedDate, setPlannedDate,
    } = useSharedState({ enabled: syncEnabled });

    // Visit history state
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [currentVisit, setCurrentVisit] = useState({ rating: null, notes: '', date: todayLocal() });
    const [visitMode, setVisitMode] = useState(null); // 'date' when logging the night of a completed date plan
    const [afterDate, setAfterDate] = useState(null); // { restaurantId } → "Guess the Check?" sheet
    const [visitRestaurantId, setVisitRestaurantId] = useState(null);
    const [editingVisitId, setEditingVisitId] = useState(null);

    // Edit restaurant state
    const [editingRestaurant, setEditingRestaurant] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', address: '', cuisine: [], vibe: [] });

    // Random picker state
    const [showRandomPicker, setShowRandomPicker] = useState(false);
    const [pickerRestaurant, setPickerRestaurant] = useState(null);
    const [isSpinning, setIsSpinning] = useState(false);

    // User location for biasing search results
    const [userLocation, setUserLocation] = useState(null);

    // Profile photo upload modal target
    const [showProfilePhotoUpload, setShowProfilePhotoUpload] = useState(null); // 'doug' or 'les'

    // Install prompt state
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);

    useEffect(() => {
        // Online/offline listeners
        const handleOnline = () => {
            setIsOnline(true);
            showToast('Back online', 'success');
        };
        const handleOffline = () => {
            setIsOnline(false);
            showToast('You are offline', 'error');
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Check for saved auth and profile
        const savedAuth = localStorage.getItem('restaurantAppAuth');
        const savedProfile = localStorage.getItem('restaurantAppProfile');

        if (savedAuth === 'true') {
            setIsAuthenticated(true);
            if (savedProfile) {
                setCurrentProfile(savedProfile);
            }
        }

        // Get user's location for biasing search results
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                () => {},
                { enableHighAccuracy: false, timeout: 10000 }
            );
        }

        // Check if running in standalone mode (added to home screen)
        const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                           window.navigator.standalone === true;

        // Install prompt logic - show after 3 visits if not installed
        if (!standalone) {
            const visitCount = parseInt(localStorage.getItem('visitCount') || '0', 10) + 1;
            localStorage.setItem('visitCount', String(visitCount));
            const dismissedInstall = localStorage.getItem('dismissedInstall');

            if (visitCount >= 3 && !dismissedInstall) {
                setTimeout(() => setShowInstallPrompt(true), 2000);
            }
        }

        // Keyboard handling for iOS - only trigger for actual keyboard (not URL bar changes)
        if (window.visualViewport) {
            const handleViewportResize = () => {
                const keyboardHeight = window.innerHeight - window.visualViewport.height;
                const activeEl = document.activeElement;
                const isInput = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT');
                if (keyboardHeight > 150 && isInput) {
                    setTimeout(() => {
                        activeEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }, 100);
                }
            };
            window.visualViewport.addEventListener('resize', handleViewportResize);
        }
    }, []);

    // Save sort preference to localStorage
    useEffect(() => {
        localStorage.setItem('sortPreference', sortBy);
    }, [sortBy]);

    // Google Places Autocomplete Widget
    const [autocompleteReady, setAutocompleteReady] = useState(false);

    useEffect(() => {
        if (!showAddForm) return undefined;

        // Bounded retries (Maps may be slow or blocked); cancelled when the form closes
        let tries = 0;
        let timer = null;
        let cancelled = false;
        const retry = (ms) => { if (!cancelled && ++tries < 40) timer = setTimeout(attachAutocomplete, ms); };
        const attachAutocomplete = () => {
            if (cancelled) return;
            if (!window.google || !window.google.maps || !window.google.maps.places) {
                retry(500);
                return;
            }

            const input = document.getElementById('restaurant-search-input');
            if (!input) {
                retry(100);
                return;
            }

            if (input.dataset.autocompleteAttached === 'true') return;

            try {
                const autocomplete = new window.google.maps.places.Autocomplete(input, {
                    fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types']
                });

                if (userLocation) {
                    const circle = new window.google.maps.Circle({
                        center: { lat: userLocation.lat, lng: userLocation.lng },
                        radius: 50000
                    });
                    autocomplete.setBounds(circle.getBounds());
                }

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place && place.name) {
                        haptic('light');
                        setNewRestaurant({
                            name: place.name,
                            address: place.formatted_address || '',
                            coordinates: place.geometry?.location ? {
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng()
                            } : null,
                            placeId: place.place_id || null,
                            placeTypes: place.types || [],
                            ...suggestCategories(place.name, place.types || [])
                        });
                    }
                });

                input.dataset.autocompleteAttached = 'true';
                setAutocompleteReady(true);
            } catch (err) {
                // Silently fail
            }
        };

        // Start trying to attach
        timer = setTimeout(attachAutocomplete, 200);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [showAddForm, userLocation]);

    // Google Places Autocomplete for Plan a Date modal
    useEffect(() => {
        if (!showPlanDateModal) return undefined;

        let tries = 0;
        let timer = null;
        let cancelled = false;
        const retry = (ms) => { if (!cancelled && ++tries < 40) timer = setTimeout(attachAutocomplete, ms); };
        const attachAutocomplete = () => {
            if (cancelled) return;
            if (!window.google || !window.google.maps || !window.google.maps.places) {
                retry(500);
                return;
            }
            const input = document.getElementById('plan-date-restaurant-input');
            if (!input) {
                retry(100);
                return;
            }
            if (input.dataset.autocompleteAttached === 'true') return;

            try {
                const ac = new window.google.maps.places.Autocomplete(input, {
                    fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types']
                });
                if (userLocation) {
                    const circle = new window.google.maps.Circle({
                        center: { lat: userLocation.lat, lng: userLocation.lng },
                        radius: 50000
                    });
                    ac.setBounds(circle.getBounds());
                }
                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    if (cancelled) return;
                    if (place && place.name) {
                        haptic('light');
                        setPlanDateForm(prev => ({
                            ...prev,
                            restaurantId: null,
                            restaurantName: place.name,
                            placeAddress: place.formatted_address || '',
                            placeCoordinates: place.geometry?.location ? {
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng()
                            } : null,
                            placeId: place.place_id || null
                        }));
                    }
                });
                input.dataset.autocompleteAttached = 'true';
            } catch (err) {}
        };

        timer = setTimeout(attachAutocomplete, 200);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            const input = document.getElementById('plan-date-restaurant-input');
            if (input) input.dataset.autocompleteAttached = '';
        };
    }, [showPlanDateModal, userLocation]);

    const handlePasscodeSubmit = () => {
        if (passcodeInput === PASSCODE) {
            setIsAuthenticated(true);
            localStorage.setItem('restaurantAppAuth', 'true');
            setShowPasscodeError(false);
            setPasscodeInput('');
            haptic('success');
        } else {
            setShowPasscodeError(true);
            setPasscodeInput('');
            setPasscodeShake(true);
            haptic('error');
            setTimeout(() => setPasscodeShake(false), 500);
        }
    };

    const handleProfileSelect = (profile) => {
        setCurrentProfile(profile);
        localStorage.setItem('restaurantAppProfile', profile);
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        setCurrentProfile(null);
        localStorage.removeItem('restaurantAppAuth');
        localStorage.removeItem('restaurantAppProfile');
    };

    // Share restaurant using Web Share API
    const shareRestaurant = async (restaurant) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: restaurant.name,
                    text: `Check out ${restaurant.name}${restaurant.address ? ' at ' + restaurant.address : ''}!`,
                    url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurant.name, restaurant.address].filter(Boolean).join(' '))}${restaurant.placeId ? `&query_place_id=${encodeURIComponent(restaurant.placeId)}` : ''}`
                });
                haptic('light');
            } catch (err) {
                // Ignore share cancellation
            }
        }
    };

    // Dismiss install prompt
    const dismissInstallPrompt = () => {
        setShowInstallPrompt(false);
        localStorage.setItem('dismissedInstall', 'true');
    };

    // ---------------------------------------------------------------- profile photos
    const saveProfilePhoto = (profile, dataUrl) => {
        const newPhotos = { ...profilePhotos, [profile]: dataUrl };
        setProfilePhotos(newPhotos);
        try { localStorage.setItem('profilePhotos', JSON.stringify(newPhotos)); } catch (e) { /* quota */ }
        db.setProfilePhotos(newPhotos).catch(onWriteError);
    };

    // Profile photo upload handler
    const handleProfilePhotoUpload = (event, profile) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            // Compress the image
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxSize = 200; // Profile pics are small
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                saveProfilePhoto(profile, canvas.toDataURL('image/jpeg', 0.8));
                setShowProfilePhotoUpload(null);
                haptic('success');
            };
            img.onerror = () => showToast("Couldn't read that photo", 'error');
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const removeProfilePhoto = (profile) => {
        saveProfilePhoto(profile, null);
        haptic('light');
    };

    // ---------------------------------------------------------------- restaurants
    const nameTaken = (name, exceptId = null) => {
        const n = name.toLowerCase().trim();
        return restaurants.some(r => !r.deletedAt && r.id !== exceptId && r.name.toLowerCase().trim() === n);
    };

    const addRestaurant = async () => {
        if (!newRestaurant.name.trim()) return;
        if (nameTaken(newRestaurant.name)) {
            showToast('Restaurant already exists', 'error');
            haptic('error');
            return;
        }

        haptic('success');
        const draft = { ...newRestaurant };
        const newId = Date.now();
        const record = {
            id: newId,
            name: draft.name.trim(),
            address: draft.address.trim() || null,
            coordinates: draft.coordinates || null,
            placeId: draft.placeId || null,
            isFavorite: false,
            notes: '',
            photo: null,
            visits: [],
            rating: null,
            checks: [],
            dateAdded: new Date().toISOString(),
            ...(draft.cuisine && draft.cuisine.length ? { cuisine: draft.cuisine } : {}),
            ...(draft.vibe && draft.vibe.length ? { vibe: draft.vibe } : {}),
            ...(draft.placeTypes && draft.placeTypes.length ? { placeTypes: draft.placeTypes } : {}),
        };
        create(record);
        setNewRestaurant({ name: '', address: '', coordinates: null, placeId: null });
        setShowAddForm(false);
        showToast('Restaurant added', 'success');

        // A typed address gets coordinates in the background (for the map preview and the Map tab)
        if (record.address && !record.coordinates) {
            geocodeAddress(record.address).then(coords => { if (coords) mutate(newId, { coordinates: coords }); });
        }

        // Scroll to the new restaurant after DOM updates
        setTimeout(() => {
            const newElement = document.getElementById(`restaurant-${newId}`);
            if (newElement) {
                newElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
        // Clear the search input
        const searchInput = document.getElementById('restaurant-search-input');
        if (searchInput) {
            searchInput.value = '';
            searchInput.dataset.autocompleteAttached = '';
        }
    };

    // ---------------------------------------------------------------- plan a date
    const savePlannedDate = (dateData) => {
        setPlannedDate(dateData);
        try { localStorage.setItem('plannedDate', JSON.stringify(dateData)); } catch (e) { /* quota */ }
        db.setPlannedDate(dateData).catch(onWriteError);
        haptic('medium');
        showToast('Date night planned! 💕', 'success');
    };

    const dropPlannedDate = () => {
        setPlannedDate(null);
        try { localStorage.removeItem('plannedDate'); } catch (e) { /* ignore */ }
        db.clearPlannedDate().catch(onWriteError);
    };

    const clearPlannedDate = () => {
        dropPlannedDate();
        haptic('light');
        showToast('Date plan cleared', 'success');
    };

    // Complete → "How was it?" (log the visit for that night) → offer Guess the Check
    const completePlannedDate = () => {
        const plan = plannedDate;
        const r = plan && restaurants.find(x => x.id === plan.restaurantId);
        if (!r) {
            dropPlannedDate();
            haptic('medium');
            showToast('Hope you had a wonderful date! 🥂', 'success');
            return;
        }
        if (r.deletedAt) mutate(r.id, { deletedAt: null });
        haptic('medium');
        setVisitRestaurantId(r.id);
        setEditingVisitId(null);
        setCurrentVisit({ rating: null, notes: '', date: isoToLocalDate(plan.dateTime) || todayLocal() });
        setVisitMode('date');
        setShowVisitModal(true);
    };

    // Called by the visit modal in 'date' mode after Save or Skip
    const finishDate = (restaurantId) => {
        dropPlannedDate();
        setVisitMode(null);
        setAfterDate({ restaurantId });
    };

    const playAfterDate = () => {
        const id = afterDate && afterDate.restaurantId;
        setAfterDate(null);
        if (!id) return;
        startCheckGame(id);
        setActiveTab('restaurants');
        setTimeout(() => {
            const el = document.getElementById(`restaurant-${id}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
    };

    const skipAfterDate = () => {
        setAfterDate(null);
        showToast('Hope you had a wonderful date! 🥂', 'success');
    };

    // ---------------------------------------------------------------- import (.txt, one name per line)
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const seen = new Set(restaurants.filter(r => !r.deletedAt).map(r => r.name.toLowerCase().trim()));
            const names = [];
            for (const line of String(e.target.result).split('\n')) {
                const name = line.trim();
                if (name && !seen.has(name.toLowerCase())) { seen.add(name.toLowerCase()); names.push(name); }
            }
            const ids = db.newIds(names.length);
            const records = names.map((name, i) => ({
                id: ids[i],
                name,
                address: null,
                coordinates: null,
                placeId: null,
                isFavorite: false,
                notes: '',
                photo: null,
                visits: [],
                rating: null,
                checks: [],
                dateAdded: new Date().toISOString()
            }));
            createMany(records);
            showToast(records.length ? `Imported ${records.length} place${records.length === 1 ? '' : 's'}` : 'Nothing new to import', 'success');
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    // Notes are saved by NotesField after you pause typing (not on every keystroke)
    const updateNotes = (id, notes) => {
        mutate(id, { notes });
    };

    // Toggle favorite
    const toggleFavorite = (id) => {
        haptic('medium');
        mutate(id, r => ({ isFavorite: !r.isFavorite }));
    };

    const toggleWantToTry = (id) => {
        haptic('light');
        mutate(id, r => ({ wantToTry: toggleWantToTryOf(r, currentProfile) }));
    };

    const setCategories = (id, { cuisine, vibe }) => {
        mutate(id, { cuisine: cuisine || [], vibe: vibe || [] });
    };

    // Geocode an address to get coordinates (for manually entered addresses)
    const geocodeAddress = (address) => {
        return new Promise((resolve) => {
            if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
                resolve(null);
                return;
            }
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    resolve({ lat: location.lat(), lng: location.lng() });
                } else {
                    resolve(null);
                }
            });
        });
    };

    // ---------------------------------------------------------------- random picker
    const spinTimer = useRef(null);
    const stopSpin = () => { clearTimeout(spinTimer.current); spinTimer.current = null; };

    const startRandomPicker = () => {
        const { pool } = pickerPool(restaurants);
        if (pool.length === 0) return;

        stopSpin();
        setShowRandomPicker(true);
        setIsSpinning(true);
        haptic('medium');

        // Spin through restaurants, slowing down like a wheel; the last pick is weighted toward double-yes places
        const delays = spinDelays(20 + Math.floor(Math.random() * 8));
        let i = 0;
        const tick = () => {
            const last = i === delays.length - 1;
            const pick = last ? weightedPick(pool) : pool[Math.floor(Math.random() * pool.length)].restaurant;
            setPickerRestaurant(pick);
            if (last) {
                setIsSpinning(false);
                haptic('success');
                spinTimer.current = null;
                return;
            }
            spinTimer.current = setTimeout(tick, delays[i++]);
        };
        tick();
    };

    const spinAgain = () => {
        setPickerRestaurant(null);
        startRandomPicker();
    };

    const closeRandomPicker = () => {
        stopSpin();
        setIsSpinning(false);
        setShowRandomPicker(false);
    };

    // ---------------------------------------------------------------- photos
    const handlePhotoUpload = (id, event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Compress to max 800px width
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const maxWidth = 800;

                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Only this restaurant's `photo` field is written
                mutate(id, { photo: canvas.toDataURL('image/jpeg', 0.7) });
                haptic('success');
            };
            img.onerror = () => showToast("Couldn't read that photo", 'error');
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const deletePhoto = (id) => {
        if (window.confirm('Delete photo?')) {
            haptic('medium');
            mutate(id, { photo: null });
        }
    };

    // ---------------------------------------------------------------- visits
    const openVisitModal = (restaurantId, date = null) => {
        setVisitRestaurantId(restaurantId);
        setEditingVisitId(null);
        setVisitMode(null);
        setCurrentVisit({ rating: null, notes: '', date: date || todayLocal() });
        setShowVisitModal(true);
    };

    const openEditVisit = (restaurantId, visit) => {
        setVisitRestaurantId(restaurantId);
        setEditingVisitId(visit.id);
        setVisitMode(null);
        setCurrentVisit({ rating: visit.rating, notes: visit.notes || '', date: visit.date });
        setShowVisitModal(true);
    };

    const closeVisitModal = () => {
        const wasDate = visitMode === 'date';
        const rid = visitRestaurantId;
        setShowVisitModal(false);
        setCurrentVisit({ rating: null, notes: '', date: todayLocal() });
        setVisitRestaurantId(null);
        setEditingVisitId(null);
        if (wasDate) finishDate(rid);
    };

    const addVisit = () => {
        if (!currentVisit.rating || !visitRestaurantId) return;
        haptic('success');
        const now = new Date().toISOString();
        let message = 'Visit logged';

        mutate(visitRestaurantId, r => {
            const visits = [...(r.visits || [])];
            const mineSameDate = visits.findIndex(v => v.date === currentVisit.date && v.loggedBy === currentProfile && v.id !== editingVisitId);

            if (editingVisitId) {
                const editIndex = visits.findIndex(v => v.id === editingVisitId);
                if (editIndex >= 0) {
                    visits[editIndex] = { ...visits[editIndex], rating: currentVisit.rating, notes: currentVisit.notes, date: currentVisit.date, timestamp: now };
                    // Moving a visit onto a date you already logged merges them
                    if (mineSameDate >= 0) visits.splice(mineSameDate, 1);
                    message = 'Visit updated';
                }
            } else if (mineSameDate >= 0) {
                // Same person already logged this date - update theirs
                visits[mineSameDate] = { ...visits[mineSameDate], rating: currentVisit.rating, notes: currentVisit.notes, timestamp: now };
                message = 'Visit updated';
            } else {
                visits.push({
                    id: Date.now(),
                    date: currentVisit.date,
                    rating: currentVisit.rating,
                    notes: currentVisit.notes,
                    loggedBy: currentProfile,
                    timestamp: now
                });
            }
            return { visits, rating: averageOfVisits(visits), dateRated: now };
        });

        showToast(message, 'success');
        closeVisitModal();
    };

    // Partner can peek at a hidden score (sets revealed on that visit)
    const revealVisit = (restaurantId, visitId) => {
        haptic('light');
        mutate(restaurantId, r => ({ visits: (r.visits || []).map(v => v.id === visitId ? { ...v, revealed: true } : v) }));
    };

    const deleteVisit = (restaurantId, visitId) => {
        if (window.confirm('Delete this visit?')) {
            haptic('medium');
            mutate(restaurantId, r => {
                const visits = (r.visits || []).filter(v => v.id !== visitId);
                return { visits, rating: averageOfVisits(visits) };
            });
        }
    };

    const deleteRestaurant = (id) => {
        const restaurant = restaurants.find(r => r.id === id);
        if (restaurant && window.confirm(`Move "${restaurant.name}" to trash?`)) {
            haptic('heavy');
            mutate(id, { deletedAt: new Date().toISOString() });
            showToast('Moved to trash', 'success');
        }
    };

    const restoreRestaurant = (id) => {
        haptic('success');
        mutate(id, { deletedAt: null });
        showToast('Restaurant restored', 'success');
    };

    const permanentlyDeleteRestaurant = (id) => {
        const restaurant = restaurants.find(r => r.id === id);
        if (restaurant && window.confirm(`Permanently delete "${restaurant.name}"?\n\nThis cannot be undone.`)) {
            haptic('heavy');
            destroy(id);
            showToast('Permanently deleted', 'success');
        }
    };

    // Per-profile theme (presets.js)
    const t = useMemo(() => themeFor(colorTheme, currentProfile), [currentProfile, colorTheme]);

    // Memoized deleted restaurants list
    const deletedRestaurants = useMemo(() => deletedRestaurantsOf(restaurants), [restaurants]);

    const openEditModal = (restaurant) => {
        setEditForm({ name: restaurant.name, address: restaurant.address || '', cuisine: restaurant.cuisine || [], vibe: restaurant.vibe || [] });
        setEditingRestaurant(restaurant);
    };

    const saveEditedRestaurant = () => {
        if (!editingRestaurant || !editForm.name.trim()) return;

        if (nameTaken(editForm.name, editingRestaurant.id)) {
            showToast('Restaurant name already exists', 'error');
            haptic('error');
            return;
        }

        haptic('success');
        const id = editingRestaurant.id;
        const address = editForm.address.trim() || null;
        const addressChanged = address !== (editingRestaurant.address || null);
        mutate(id, {
            name: editForm.name.trim(),
            address,
            cuisine: editForm.cuisine || [],
            vibe: editForm.vibe || [],
            ...(addressChanged ? { coordinates: null, placeId: null } : {}),
        });
        if (addressChanged && address) {
            geocodeAddress(address).then(coords => { if (coords) mutate(id, { coordinates: coords }); });
        }
        setEditingRestaurant(null);
        showToast('Restaurant updated', 'success');
    };

    const openInMaps = (address) => {
        const encodedAddress = encodeURIComponent(address);
        // Apple Maps on iOS, Google Maps elsewhere
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const mapsUrl = isIOS
            ? `maps://maps.apple.com/?q=${encodedAddress}`
            : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapsUrl, '_blank');
    };

    // ---------------------------------------------------------------- guess the check
    // Start a new Guess the Check game (creates the shared Firebase session)
    const startCheckGame = (restaurantId) => {
        const restaurant = restaurants.find(r => r.id === restaurantId);
        if (!restaurant) return;

        const gameData = {
            restaurantId: restaurantId,
            restaurantName: restaurant.name,
            status: 'guessing', // guessing → confirm → complete
            guesses: { doug: null, les: null },
            actual: null,
            winner: null,
            startedBy: currentProfile,
            startedAt: Date.now()
        };

        setActiveGame(gameData);
        db.setActiveGame(gameData).catch(onWriteError);
        setExpandedId(restaurantId);
        haptic('medium');
    };

    // Submit my guess (only for my profile). $0 is a valid guess.
    const submitMyGuess = () => {
        const guessValue = parseAmount(myGuess);
        if (guessValue === null || !activeGame) {
            if (activeGame) showToast('Enter an amount like 42.50', 'error');
            return;
        }
        db.setGuess(currentProfile, guessValue).catch(onWriteError);
        setMyGuess('');
        haptic('light');
    };

    // Doug enters the actual amount and moves to confirm
    const submitActual = () => {
        const actual = parseAmount(actualAmount);
        if (actual === null || !activeGame || currentProfile !== 'doug') {
            if (activeGame && currentProfile === 'doug') showToast('Enter the check total like 86.40', 'error');
            return;
        }
        db.patchActiveGame({ actual, status: 'confirm' }).catch(onWriteError);
        haptic('light');
    };

    const backToGuessing = () => {
        db.patchActiveGame({ status: 'guessing', actual: null }).catch(onWriteError);
    };

    // Confirm and save the game result (compared in cents)
    const confirmAndSave = () => {
        if (!activeGame || !isAmount(activeGame.actual)) return;

        const dougGuess = isAmount(activeGame.guesses?.doug) ? activeGame.guesses.doug : 0;
        const lesGuess = isAmount(activeGame.guesses?.les) ? activeGame.guesses.les : 0;
        const actual = activeGame.actual;
        const { dougDiff, lesDiff, winner } = checkWinner(dougGuess, lesGuess, actual);

        const checkData = {
            id: Date.now(),
            dougGuess,
            lesGuess,
            actualAmount: actual,
            dougDiff,
            lesDiff,
            winner,
            date: new Date().toISOString()
        };

        mutate(activeGame.restaurantId, r => ({ checks: [...(r.checks || []), checkData] }));
        db.patchActiveGame({ status: 'complete', winner }).catch(onWriteError);

        haptic('success');
        setResultCountdown(5);
    };

    // Close/cancel the game
    const closeGame = () => {
        setActiveGame(null);
        db.clearActiveGame().catch(onWriteError);
        setMyGuess('');
        setActualAmount('');
        setResultCountdown(null);
    };

    // Countdown timer for results screen
    useEffect(() => {
        if (resultCountdown === null || resultCountdown <= 0) {
            if (resultCountdown === 0 && activeGame?.status === 'complete') {
                closeGame();
            }
            return;
        }
        const timer = setTimeout(() => {
            setResultCountdown(resultCountdown - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [resultCountdown, activeGame]);

    // Memoized sorted restaurants list (lib/sort.js)
    const sortedRestaurants = useMemo(() => sortRestaurants(restaurants, { sortBy, query: searchQuery, unratedOnly: showUnratedOnly, wantFilter: wantFilter === 'both' ? 'both' : 'none', cuisine: cuisineFilter, vibe: vibeFilter }), [restaurants, searchQuery, showUnratedOnly, sortBy, wantFilter, cuisineFilter, vibeFilter]);

    // Memoized quick-stat numbers (lib/stats.js)
    const averageRating = useMemo(() => averageRatingOf(restaurants), [restaurants]);
    const ratedCount = useMemo(() => ratedCountOf(restaurants), [restaurants]);
    const activeRestaurantCount = useMemo(() => activeRestaurantCountOf(restaurants), [restaurants]);

    // Memoized check stats (lib/stats.js)
    const stats = useMemo(() => computeStats(restaurants), [restaurants]);

    // Everything the screen/section components read via useApp(): state, setters, handlers, computed values, theme.
    const app = {
        // theme + feedback
        t, haptic, showToast, toast, isOnline,
        // auth + profile
        currentProfile, handleProfileSelect, handleLogout,
        passcodeInput, setPasscodeInput, passcodeShake, showPasscodeError, handlePasscodeSubmit,
        profilePhotos, setProfilePhotos, showProfilePhotoUpload, setShowProfilePhotoUpload, handleProfilePhotoUpload, removeProfilePhoto,
        // restaurants + derived
        restaurants, setRestaurants, mutate, create, createMany, destroy, isSyncing, sync, saveProfilePhoto,
        sortedRestaurants, deletedRestaurants, stats, averageRating, ratedCount, activeRestaurantCount,
        // navigation / view state
        activeTab, setActiveTab, showSettings, setShowSettings, showTrash, setShowTrash, expandedId, setExpandedId,
        showInstallPrompt, dismissInstallPrompt,
        // appearance settings
        colorTheme, setColorTheme, showFloatingBg, setShowFloatingBg, floatingEmoji, setFloatingEmoji,
        // list controls
        sortBy, setSortBy, searchQuery, setSearchQuery, showUnratedOnly, setShowUnratedOnly,
        wantFilter, setWantFilter, cuisineFilter, setCuisineFilter, vibeFilter, setVibeFilter,
        // add place
        showAddForm, setShowAddForm, newRestaurant, setNewRestaurant, autocompleteReady, addRestaurant, handleFileUpload,
        // per-restaurant actions
        toggleFavorite, shareRestaurant, openEditModal, deleteRestaurant, restoreRestaurant, permanentlyDeleteRestaurant,
        handlePhotoUpload, deletePhoto, updateNotes, openInMaps, toggleWantToTry, setCategories, geocodeAddress,
        // visits
        showVisitModal, setShowVisitModal, currentVisit, setCurrentVisit, editingVisitId, setEditingVisitId,
        openVisitModal, openEditVisit, addVisit, deleteVisit, revealVisit, closeVisitModal, visitMode, visitRestaurantId, finishDate,
        // edit restaurant
        editingRestaurant, setEditingRestaurant, editForm, setEditForm, saveEditedRestaurant,
        // plan a date
        plannedDate, showPlanDateModal, setShowPlanDateModal, planDateForm, setPlanDateForm,
        planDateSearch, setPlanDateSearch, savePlannedDate, clearPlannedDate, completePlannedDate,
        afterDate, playAfterDate, skipAfterDate,
        // random picker
        showRandomPicker, setShowRandomPicker, pickerRestaurant, isSpinning, startRandomPicker, spinAgain, closeRandomPicker,
        // guess the check
        activeGame, myGuess, setMyGuess, actualAmount, setActualAmount, resultCountdown,
        startCheckGame, submitMyGuess, submitActual, confirmAndSave, closeGame, backToGuessing,
    };

    // Passcode Screen
    if (!isAuthenticated) {
        return (
            <AppContext.Provider value={app}>
                <PasscodeScreen />
            </AppContext.Provider>
        );
    }

    // Profile Selection Screen
    if (!currentProfile) {
        return (
            <AppContext.Provider value={app}>
                <ProfilePicker />
            </AppContext.Provider>
        );
    }

    // Main App
    return (
        <AppContext.Provider value={app}>
        <div className={`min-h-screen bg-gradient-to-br ${t.bg} pb-24 ${t.themeClass}`}>
            {/* Toast Notification */}
            <Toast />

            {/* Offline / install / game banners, stacked under the notch */}
            <TopBanners />

            {/* Floating Background */}
            <FloatingBg />

            <div className="max-w-lg mx-auto px-4">
                {showSettings ? (
                    <SettingsPanel />
                ) : (
                <>
                {/* Header - Sticky on mobile */}
                <Header />

                {activeTab === 'restaurants' && (
                    <>
                        {/* Quick Stats */}
                        <QuickStats />

                        {/* Plan a Date */}
                        <PlannedDateCard />

                        {/* 1. Search Bar */}
                        <SearchBar />

                        {/* Show Add Form OR Controls + List */}
                        {showAddForm ? (
                            /* Add Restaurant Form - Inline View */
                            <AddPlaceForm />
                        ) : (
                            /* Normal View - Controls + Sort + List */
                            <>
                                {/* Add Controls */}
                                <SortControls />

                                {/* Trash View */}
                                {showTrash ? (
                                    <TrashList />
                                ) : (
                                    /* Restaurant List */
                                    <RestaurantList />
                                )}
                            </>
                        )}
                    </>
                )}

                {activeTab === 'map' && (
                    <MapView />
                )}

                {activeTab === 'stats' && (
                    <StatsTab />
                )}

                {/* Visit Logging Modal */}
                <VisitModal />

                {/* Edit Restaurant Modal */}
                <EditRestaurantModal />

                {/* Plan a Date Modal */}
                <PlanDateModal />

                {/* Random Picker Modal */}
                <RandomPickerModal />

                {/* After a completed date: Guess the Check? */}
                <AfterDateSheet />
                </>
                )}
            </div>

            {/* Profile photo picker (also opened from Settings) */}
            <ProfilePhotoModal />
        </div>
        </AppContext.Provider>
    );
}

export default RestaurantRater;
